package com.havn.app.core.platform

import kotlinx.browser.window

actual object HavnExternalActions {
    actual fun openUrl(url: String): Boolean =
        openBrowserUrl(url)

    actual fun openPhone(to: String): Boolean =
        openBrowserUrl("tel:${to.trim()}")

    actual fun openEmail(to: String, subject: String, body: String): Boolean =
        openBrowserUrl("mailto:${to.trim()}?subject=${encode(subject)}&body=${encode(body)}")

    actual fun openEmailWithAttachments(to: String, subject: String, body: String, attachments: List<HavnEmailAttachment>): Boolean =
        openEmail(to, subject, body)

    actual fun openEmailWithAttachments(to: List<String>, subject: String, body: String, attachments: List<HavnEmailAttachment>): Boolean =
        openEmail(to.filter { it.isNotBlank() }.joinToString(","), subject, body)

    actual fun openEmailWithPhotos(to: String, subject: String, body: String, photoDataUrls: List<String>): Boolean =
        openEmail(to, subject, body.withPublicPhotoLinks(photoDataUrls))

    actual fun openSms(to: String, body: String): Boolean =
        openBrowserUrl("sms:${to.trim()}?body=${encode(body)}")

    actual fun openSmsWithPhotos(to: String, body: String, photoDataUrls: List<String>): Boolean {
        val bodyWithLinks = body.withPublicPhotoLinks(photoDataUrls)
        return sharePhotos(
            title = "HAVN photos",
            text = bodyWithLinks,
            photoDataUrls = photoDataUrls.toTypedArray()
        ) || openSms(to, bodyWithLinks)
    }

    actual fun openNavigation(latitude: Double, longitude: Double): Boolean =
        openBrowserUrl("https://www.google.com/maps/dir/?api=1&destination=$latitude,$longitude")

    actual fun shareQrImage(
        title: String,
        message: String,
        fileName: String,
        payload: String,
        email: String?,
        phone: String?
    ): Boolean {
        val targetEmail = email?.trim().orEmpty()
        return if (targetEmail.isNotBlank()) {
            openEmail(
                to = targetEmail,
                subject = title,
                body = "$message\n\n$payload"
            )
        } else {
            false
        }
    }

    private fun openBrowserUrl(url: String): Boolean =
        window.open(url, "_blank") != null
}

@Suppress("UnsafeCastFromDynamic")
private fun encode(value: String): String = js("encodeURIComponent(value)") as String

@Suppress("UnsafeCastFromDynamic")
private fun sharePhotos(title: String, text: String, photoDataUrls: Array<String>): Boolean = js(
    """
    (() => {
        const nav = window.navigator;
        if (!nav || typeof nav.share !== "function") return false;

        const files = [];
        for (let i = 0; i < photoDataUrls.length; i += 1) {
            const value = String(photoDataUrls[i] || "");
            if (!value.startsWith("data:image/")) continue;

            const commaIndex = value.indexOf(",");
            if (commaIndex < 0) continue;

            const meta = value.slice(5, commaIndex);
            const mimeType = meta.split(";")[0] || "image/jpeg";
            const base64 = value.slice(commaIndex + 1);
            let binary;
            try {
                binary = window.atob(base64);
            } catch (_) {
                continue;
            }

            const bytes = new Uint8Array(binary.length);
            for (let j = 0; j < binary.length; j += 1) {
                bytes[j] = binary.charCodeAt(j);
            }

            const extension = mimeType.includes("png")
                ? "png"
                : mimeType.includes("webp")
                    ? "webp"
                    : mimeType.includes("avif")
                        ? "avif"
                        : "jpg";
            files.push(new File([bytes], "havn-property-photo-" + (files.length + 1) + "." + extension, { type: mimeType }));
        }

        if (files.length === 0) return false;
        if (typeof nav.canShare === "function" && !nav.canShare({ files })) return false;

        const payload = { title, text, files };
        Promise.resolve(nav.share(payload)).catch(() => {});
        return true;
    })()
    """
) as Boolean

private fun String.withPublicPhotoLinks(photoDataUrls: List<String>): String {
    val links = photoDataUrls
        .filter { it.startsWith("http://", ignoreCase = true) || it.startsWith("https://", ignoreCase = true) }
        .distinct()
    if (links.isEmpty()) return this
    return buildString {
        append(this@withPublicPhotoLinks.trim())
        append("\n\nSelected photos:")
        links.forEachIndexed { index, link ->
            append("\n")
            append(index + 1)
            append(". ")
            append(link)
        }
    }
}
